import * as actionTypes from './TreeActions';
import axios from 'axios';
import store from '../../../index';
import { backend_server_address } from '../../../backendServer';
import {
    MachineStatus,
    MergeFleetStatusAction,
    MyThunkDispatch,
    SetOpenAssetAction,
    StatusEntry,
    StatusMap,
} from './treeTypes';

// Field set the open asset drawer needs on top of the base poll: the per-domain conditions it shows
// as coverage chips, plus the sparkline behind the health-score trend. No other poll carries
// `health_score_detail`.
export const OPEN_ASSET_DETAIL = 'conditions,health_score';

type FleetStatusResponse = { server_now: string; node_id: number; machines: StatusEntry[] };

// Merge per-machine status entries ({node_id, health, running, on_seconds_estimate}) into the
// `status` slice. Unchanged entries keep their object identity, and if nothing changed the previous
// map reference is returned untouched — so a per-machine subscriber (`status[id]`) only re-renders
// when its own machine changes, and a no-op update re-renders nothing.
const sameStatus = (a: MachineStatus, b: MachineStatus): boolean =>
    a.health === b.health &&
    a.lifecycle_state === b.lifecycle_state &&
    a.health_score === b.health_score &&
    a.observability === b.observability &&
    a.running === b.running &&
    a.on_seconds_estimate === b.on_seconds_estimate &&
    JSON.stringify(a.coverage) === JSON.stringify(b.coverage) &&
    JSON.stringify(a.offline) === JSON.stringify(b.offline) &&
    JSON.stringify(a.domains_enabled) === JSON.stringify(b.domains_enabled) &&
    JSON.stringify(a.health_score_detail) === JSON.stringify(b.health_score_detail);

export const mergeStatusEntries = (prev: StatusMap, entries: StatusEntry[]): StatusMap => {
    const status: StatusMap = { ...prev };
    let changed = false;
    for (const e of entries ?? []) {
        const cur = prev?.[e.node_id];
        // Overlay only the fields the entry actually carries. Different polls return different field
        // sets: the base fleet poll carries the scalars (health/running/…) but omits coverage/offline,
        // while the group `detail=conditions` poll carries coverage/offline but may omit the scalars.
        // Guarding every field with `in` keeps each poll from wiping values it didn't report — e.g. a
        // conditions poll must not blank the `running` state the base poll set. The wire field is
        // `conditions`; it's stored as `coverage`.
        const next: MachineStatus = { ...cur };
        if ('health' in e) next.health = e.health;
        if ('lifecycle_state' in e) next.lifecycle_state = e.lifecycle_state;
        if ('health_score' in e) next.health_score = e.health_score;
        if ('observability' in e) next.observability = e.observability;
        if ('running' in e) next.running = e.running;
        if ('on_seconds_estimate' in e) next.on_seconds_estimate = e.on_seconds_estimate;
        if ('conditions' in e) next.coverage = e.conditions;
        if ('offline' in e) next.offline = e.offline;
        if ('domains_enabled' in e) next.domains_enabled = e.domains_enabled;
        if ('health_score_detail' in e) next.health_score_detail = e.health_score_detail;
        if (!cur || !sameStatus(cur, next)) {
            status[e.node_id] = next;
            changed = true;
        }
    }
    return changed ? status : (prev ?? {});
};

// Live machine status lives in a `status` slice keyed by node id, kept deliberately separate from
// `tree`/`treeMap` so updates never give the static structure a new reference (maps, layout,
// clustering must not re-render). The slice is seeded from the tree on `getTreeSuccess` and refreshed
// by this poll, so consumers read `status` alone (no fallback to `node.data`).
export const mergeFleetStatus = (machines: StatusEntry[]): MergeFleetStatusAction => {
    const status = mergeStatusEntries(store.getState().nodeTreeReducers.status ?? {}, machines);
    // statusUpdatedAt always advances (it's the last-refreshed time), even when no value changed — so
    // the "actualizado" chip resets while the card subscribers (status[id]) stay put on a no-op poll.
    return { type: actionTypes.MERGE_FLEET_STATUS, status, statusUpdatedAt: new Date().toISOString() };
};

// Poll the whole fleet's live status (no node_id -> backend defaults to the user's base node) and
// merge it into the status slice. The static tree is fetched once (getTree); this keeps machine
// status fresh on a ~5 min cadence without refetching or re-rendering structure.
export const pollFleetStatus = () => {
    return (dispatch: MyThunkDispatch) => {
        axios
            .get<FleetStatusResponse>(backend_server_address + '/api/equipment_nodes/fleet_status/', {
                withCredentials: true,
            })
            .then((res) => dispatch(mergeFleetStatus(res.data.machines)))
            .catch(() => {
                // Transient poll failure: skip this tick; the next poll retries.
            });
    };
};

// Per-group conditions: fetch the live per-domain conditions + offline status for a group's
// DIRECT-child machines (`?node_id=<group>&detail=conditions`) and merge into the same status slice.
// Scoped to the group so the payload stays small (~20 machines).
// Returns the underlying request promise (always resolves, never rejects — the `.catch()`
// below swallows failures) so callers that need to know when the poll *settles*, success or
// error, can await it (e.g. a row deciding when it's done trying to load its own data).
// Existing callers that only dispatch this and ignore the return value are unaffected.
export const pollGroupConditions = (nodeId: number, detail = 'conditions') => {
    return (dispatch: MyThunkDispatch) => {
        return axios
            .get<FleetStatusResponse>(backend_server_address + '/api/equipment_nodes/fleet_status/', {
                withCredentials: true,
                params: { node_id: nodeId, detail },
            })
            .then((res) => dispatch(mergeFleetStatus(res.data.machines)))
            .catch(() => {
                // Transient failure: the next periodic tick / navigation retries.
            });
    };
};

// The group whose machine cards are on screen: the current node when it's a group with machines, or —
// when a machine summary drawer is open (including a direct deep-link to a machine) — the machine's
// parent group. Stable across opening/closing the drawer (group → machine → group all resolve here to
// the same group), so callers can key off it to avoid redundant fetches. undefined when no group applies.
export const displayedGroupId = (currentNode: any): number | undefined => {
    if (!currentNode) return undefined;
    if (currentNode.type === 'MACHINE') return currentNode.parent_id ?? undefined;
    const hasMachines = (currentNode.children ?? []).some((child: any) => child.type === 'MACHINE');
    return hasMachines ? currentNode.id : undefined;
};

// The machine whose asset drawer is open, tracked so the periodic refresh can keep it in sync with
// the cards behind it. `displayedGroupId` doesn't cover it: the drawer can be opened from the feed on
// a machine belonging to any group, and a group's poll never carries `health_score_detail` anyway.
export const setOpenAsset = (openAssetId: number | null): SetOpenAssetAction => ({
    type: actionTypes.SET_OPEN_ASSET,
    openAssetId,
});

// Refresh the open drawer's asset, if any, with the fields only it needs.
export const pollOpenAssetConditions = () => {
    return (dispatch: MyThunkDispatch) => {
        const openAssetId = store.getState().nodeTreeReducers.openAssetId;
        if (openAssetId != null) dispatch(pollGroupConditions(openAssetId, OPEN_ASSET_DETAIL));
    };
};

// Fetch conditions for the displayed group. Used on group change and on the ~5 min periodic refresh.
export const pollCurrentGroupConditions = () => {
    return (dispatch: MyThunkDispatch) => {
        const groupId = displayedGroupId(store.getState().nodeTreeReducers.currentNode);
        if (groupId != null) dispatch(pollGroupConditions(groupId));
    };
};
